import React from 'react';

interface LegalDocProps {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
}

const LegalLayout: React.FC<LegalDocProps> = ({ title, lastUpdated, children }) => {
  return (
    <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 max-w-4xl mx-auto animate-fade-in">
      <h1 className="text-3xl font-bold text-slate-900 mb-2">{title}</h1>
      <p className="text-sm text-slate-500 mb-8 border-b border-slate-100 pb-4">Last Updated: {lastUpdated}</p>
      <div className="prose prose-slate max-w-none text-slate-700 headings:text-slate-800 headings:font-semibold">
        {children}
      </div>
    </div>
  );
};

export const PrivacyPolicy: React.FC = () => (
  <LegalLayout title="Privacy Policy" lastUpdated={new Date().toLocaleDateString()}>
    <p>At EpochTimer, we prioritize the privacy of our visitors. This Privacy Policy document contains types of information that is collected and recorded by EpochTimer and how we use it.</p>
    
    <h3>Data Collection</h3>
    <p>EpochTimer is a <strong>client-side application</strong>. This means that all calculations (timestamp conversions, date math, etc.) happen directly in your web browser. We do not send your input data (such as dates, times, or custom epoch values) to a backend server for processing or storage.</p>
    
    <h3>Log Files</h3>
    <p>Like many other Web sites, EpochTimer may use standard web server log files. These files merely log visitors to the site - usually a standard procedure for hosting companies and a part of hosting services's analytics. The information inside the log files includes internet protocol (IP) addresses, browser type, Internet Service Provider (ISP), date/time stamp, referring/exit pages, and possibly the number of clicks. This information is used to analyze trends, administer the site, track user's movement around the site, and gather demographic information. IP addresses and other such information are not linked to any information that is personally identifiable.</p>
    
    <h3>Third-Party Services</h3>
    <p>We may use third-party libraries (e.g., via CDNs) to provide functionality. These third parties may collect request data (such as IP addresses) as part of their standard service logs to ensure security and performance.</p>
  </LegalLayout>
);

export const TermsOfService: React.FC = () => (
  <LegalLayout title="Terms of Service" lastUpdated={new Date().toLocaleDateString()}>
    <h3>1. Terms</h3>
    <p>By accessing this Website, accessible from epochtimer.com, you are agreeing to be bound by these Website Terms and Conditions of Use and agree that you are responsible for the agreement with any applicable local laws. If you disagree with any of these terms, you are prohibited from accessing this site.</p>
    
    <h3>2. Use License</h3>
    <p>Permission is granted to temporarily download one copy of the materials on EpochTimer's Website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:</p>
    <ul>
      <li>modify or copy the materials;</li>
      <li>use the materials for any commercial purpose or for any public display;</li>
      <li>attempt to reverse engineer any software contained on EpochTimer's Website;</li>
      <li>remove any copyright or other proprietary notations from the materials; or</li>
      <li>transfer the materials to another person or "mirror" the materials on any other server.</li>
    </ul>
    
    <h3>3. Disclaimer</h3>
    <p>All the materials on EpochTimer's Website are provided "as is". EpochTimer makes no warranties, may it be expressed or implied, therefore negates all other warranties. Furthermore, EpochTimer does not make any representations concerning the accuracy or reliability of the use of the materials on its Website or otherwise relating to such materials or any sites linked to this Website.</p>
    
    <h3>4. Limitations</h3>
    <p>EpochTimer or its suppliers will not be hold accountable for any damages that will arise with the use or inability to use the materials on EpochTimer's Website, even if EpochTimer or an authorize representative of this Website has been notified, orally or written, of the possibility of such damage.</p>
  </LegalLayout>
);

export const CookiePolicy: React.FC = () => (
    <LegalLayout title="Cookie Policy" lastUpdated={new Date().toLocaleDateString()}>
        <p>This is the Cookie Policy for EpochTimer, accessible from epochtimer.com.</p>
        
        <h3>What Are Cookies</h3>
        <p>As is common practice with almost all professional websites this site uses cookies, which are tiny files that are downloaded to your computer, to improve your experience. This page describes what information they gather, how we use it and why we sometimes need to store these cookies.</p>
        
        <h3>How We Use Cookies</h3>
        <p>We use cookies for a variety of reasons detailed below. Unfortunately in most cases there are no industry standard options for disabling cookies without completely disabling the functionality and features they add to this site.</p>

        <h3>The Cookies We Set</h3>
        <ul>
            <li>
                <strong>Site preferences cookies:</strong> In order to provide you with a great experience on this site we provide the functionality to set your preferences for how this site runs when you use it. In order to remember your preferences we need to set cookies (or use Local Storage) so that this information can be called whenever you interact with a page is affected by your preferences.
            </li>
        </ul>
        
        <h3>Third Party Cookies</h3>
        <p>In some special cases we also use cookies provided by trusted third parties. This site may use Google Analytics or similar services which are one of the most widespread and trusted analytics solution on the web for helping us to understand how you use the site and ways that we can improve your experience.</p>
    </LegalLayout>
);

export const LegalNotice: React.FC = () => (
    <LegalLayout title="Legal Notice" lastUpdated={new Date().toLocaleDateString()}>
        <h3>Ownership</h3>
        <p>EpochTimer is a developer utility tool created to assist with time-based calculations. All intellectual property rights concerning the design and unique functionality of this website belong to the creators of EpochTimer.</p>
        
        <h3>Accuracy of Information</h3>
        <p>While we strive to ensure the accuracy of the calculations and data presented on this site (such as leap seconds, time zone offsets, and epoch conversions), time standards can be complex and subject to change (e.g., IANA Time Zone Database updates). Users should verify critical calculations independently.</p>
        
        <h3>Contact</h3>
        <p>If you have any legal questions or concerns regarding this website, please contact us via the administrative contacts associated with the domain registration.</p>
    </LegalLayout>
);